import { countBy, Dictionary, get, isArray, orderBy, sumBy } from 'lodash';
import { PositionType, Sectors, TopPositionsType } from '../constants/lookups';
import { Sector } from '../types';
import { Account } from '../types/Account.type';
import { PositionWithExtraInfo } from '../types/Portfolio.type';
import {
    AccountPositionBreakdownStat,
    AccountSecurityTypeBreakdownStat,
    AccountSizesBreakdownStat,
    AccountTopPositionsBreakdownStat,
    AccountTypesBreakdownStat,
    BondStat,
    QuoteHistory,
    QuoteStat,
    SectorsBreakdownStat,
    TopPositionsStat,
} from '../types/Stat.type';
import { AccountWithExtraInfo } from './../types/Account.type';
import { PositionWithIndustryType } from './../types/Portfolio.type';
import { divide, multiply, num, subtract, sum } from './numberUtils';
import { parseOptionSymbol, titleCase } from './stringUtils';

export function formatAccountPositionTypeBreakdownStat(
    positions: PositionWithExtraInfo[],
): AccountPositionBreakdownStat {
    const count = countBy(positions, (position) => position.type);
    const total = sum(...Object.values(count));
    const labels = Object.keys(count).sort();
    const labelValues: Dictionary<string> = {
        [PositionType.Cash]: 'Cash',
        [PositionType.Margin]: 'Margin',
        [PositionType.Short]: 'Short',
    };
    return labels.map((label) => ({
        label: titleCase(labelValues[num(label).value()] || ''),
        value: num(count[label]).divide(total).value(),
    }));
}

export function formatAccountSecurityTypeBreakdownStat(
    positions: PositionWithExtraInfo[],
): AccountSecurityTypeBreakdownStat {
    const count = countBy(positions, (position) => position.securityType);
    const total = sum(...Object.values(count));
    const labels = Object.keys(count).sort();
    return labels.map((label) => ({
        label,
        value: num(count[label]).divide(total).value(),
    }));
}

export function formatAccountTypesBreakdownStat(data: Account[]): AccountTypesBreakdownStat;
export function formatAccountTypesBreakdownStat(data: Dictionary<number>): AccountTypesBreakdownStat;
export function formatAccountTypesBreakdownStat(data: Account[] | Dictionary<number>): AccountTypesBreakdownStat {
    let count: Dictionary<number> = {};
    if (isArray(data)) {
        count = countBy(data, (account) => account.accountClassification);
    } else {
        count = data;
    }

    const total = sum(...Object.values(count));
    const labels = Object.keys(count).sort();

    return labels.map((label) => ({
        label: titleCase(label).replace('Ira', 'IRA'),
        value: num(count[label]).divide(total).value(),
    }));
}

export function formatAccountSizesBreakdownStat(
    accountList: AccountWithExtraInfo[],
    bucket = 25000,
): AccountSizesBreakdownStat {
    const step = bucket / 1000;
    const accountSizesBreakdown = countBy(
        accountList.filter((account) => account.balance),
        (account) => (Math.floor(account.balance / bucket) + 1) * step,
    );

    return Object.keys(accountSizesBreakdown).map((key) => ({
        label: key,
        value: accountSizesBreakdown[key]!,
    }));
}

export function formatQuoteStat(data: QuoteHistory[], etfData: QuoteHistory[]): QuoteStat {
    // only has current quote, no historical data
    if (data.length === 1) {
        return [];
    }

    const firstPrice = data[0]?.price || 0;
    const firstEtfPrice = etfData[0]?.price || 0;

    function reduceQuoteHistory(data: QuoteHistory[], days = 30): QuoteHistory[] {
        const step = Math.round(divide(data.length, days));

        if (step < 1) {
            return data;
        }

        const reducedData = data.reduce<QuoteHistory[]>((result, item, index) => {
            if (index % step === 0 || index === data.length - 1) {
                result.push(item);
            }
            return result;
        }, []);

        return reducedData;
    }

    return reduceQuoteHistory(data).map((item) => {
        const etfItem = etfData.find((etf) => etf.date === item.date);
        const formattedDate = item.date || '';
        const price = item.price || 0;
        const value = price ? subtract(multiply(divide(price, firstPrice), 100), 100) : 0;
        const etfPrice = etfItem?.price ?? 0;
        const etfValue = subtract(multiply(divide(etfPrice, firstEtfPrice), 100), 100);
        return {
            date: formattedDate,
            label: formattedDate,
            value,
            etfValue,
            price,
            etfPrice,
        };
    });
}

export function formatBondStat(data: BondStat[]): QuoteStat {
    const firstPrice = data[0]?.price || 0;
    return data.map((stat) => {
        return {
            date: stat.timestamp,
            label: stat.timestamp,
            value: subtract(multiply(divide(stat.price, firstPrice), 100), 100),
            price: stat.price,
        };
    });
}

export function formatTopPositionsStat(
    positions: PositionWithExtraInfo[],
    by: TopPositionsType,
    maxPositions: number,
): TopPositionsStat {
    const allSortedPositions = orderBy([...positions], [by], 'desc');
    const sortedPositions = allSortedPositions.slice(0, maxPositions);
    const labels = sortedPositions.map((position) => {
        const optionInfo = parseOptionSymbol(position.symbol);
        const option = `${optionInfo?.symbol} ${optionInfo?.call ? 'Call' : 'Put'}`;
        return [position.option ? option : position.symbol, position.accountNumber];
    });

    const values = sortedPositions.map((position) => get(position, by));

    return sortedPositions.map((_label, index) => ({
        label: labels[index]!,
        value: values[index]!,
    }));
}

export function formatTopPositionsBreakdownChartData(
    positions: PositionWithExtraInfo[],
    topPositions = 5,
): AccountTopPositionsBreakdownStat {
    if (positions.length === 0) {
        return [];
    }
    // absolute value for short positions
    const total = sumBy(positions, (positions) => Math.abs(positions.value));
    const filteredPositions = orderBy(positions, (position) => Math.abs(position.value), ['desc']).slice(
        0,
        topPositions,
    );
    const data = filteredPositions.map((item) => ({
        label: item.symbol,
        value: divide(Math.abs(item.value), total),
    }));

    return data;
}

export function formatSectorsBreakdownStat(
    sectors: PositionWithIndustryType[],
    groups: PositionWithIndustryType[],
    etfSectors?: Sector[][],
): SectorsBreakdownStat {
    const symbolToSectorMap: Dictionary<string> = {};

    const sectorsBreakdown = sectors.reduce<
        Dictionary<{
            value: number;
            groups: Dictionary<number>;
        }>
    >((temp, item) => {
        const sector = titleCase(item.industryByType.industryName);
        symbolToSectorMap[item.symbol] = sector;

        if (temp.hasOwnProperty(sector)) {
            temp[sector]!.value += item.value;
        } else {
            temp[sector] = {
                value: item.value,
                groups: {},
            };
        }

        return temp;
    }, {});

    groups.reduce((temp, item) => {
        const group = titleCase(item.industryByType.industryName);
        const sector = symbolToSectorMap[item.symbol]!;

        if (temp[sector]!.groups.hasOwnProperty(group)) {
            temp[sector]!.groups[group]! += item.value;
        } else {
            temp[sector]!.groups[group] = item.value;
        }

        return temp;
    }, sectorsBreakdown);

    const totalSectorsValue = sumBy(Object.values(sectorsBreakdown), (item) => item.value);

    return Object.keys(sectorsBreakdown).map((sectorKey) => {
        const sector = sectorsBreakdown[sectorKey]!;
        const value = num(sector.value).divide(totalSectorsValue).value();

        // EtfSectors - optional param. Others in this case would be ETFs
        if (etfSectors && sectorKey === Sectors.Others) {
            const finalGroups: Sector[] = [];
            etfSectors.forEach((sector) => {
                if (sector.length > 0) {
                    sector.forEach((item) => {
                        const group = finalGroups.find((group) => group.code === item.code);
                        if (!group) {
                            finalGroups.push(item);
                        } else if (group.percentage) {
                            group.percentage += item.percentage || 0;
                        }
                    });
                }
            });
            const totalGroupValues = sumBy(finalGroups, (group) => group.percentage!);
            return {
                label: 'ETFs',
                value: value,
                groups: finalGroups.map((sector) => ({
                    label: sector.breakdown || '',
                    value: multiply(divide(sector.percentage || 0, totalGroupValues), value),
                })),
            };
        }

        return {
            label: sectorKey,
            value,
            groups: Object.keys(sector.groups).map((groupKey) => ({
                label: groupKey || '',
                value: num(sector.groups[groupKey]).divide(totalSectorsValue).value(),
            })),
        };
    });
}
